import * as React from 'react'
import * as DropdownMenuPrimitives from '@radix-ui/react-dropdown-menu'
import { RiArrowRightSLine, RiCheckLine, RiRadioButtonFill, RiCheckboxBlankCircleLine } from '@remixicon/react'
import { cx } from '../../lib/utils'

const DropdownMenu = DropdownMenuPrimitives.Root
const DropdownMenuTrigger = DropdownMenuPrimitives.Trigger
const DropdownMenuGroup = DropdownMenuPrimitives.Group
const DropdownMenuSubMenu = DropdownMenuPrimitives.Sub
const DropdownMenuRadioGroup = DropdownMenuPrimitives.RadioGroup

const DropdownMenuSubMenuTrigger = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.SubTrigger>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.SubTrigger>
>(({ className, children, ...props }, ref) => (
  <DropdownMenuPrimitives.SubTrigger
    ref={ref}
    className={cx(
      'relative flex cursor-default select-none items-center rounded py-1.5 pl-2 pr-1 outline-none transition-colors sm:text-sm',
      'text-gray-900 dark:text-gray-50',
      'data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 dark:data-[disabled]:text-gray-600',
      'focus-visible:bg-gray-100 data-[state=open]:bg-gray-100 dark:focus-visible:bg-gray-900 dark:data-[state=open]:bg-gray-900',
      'hover:bg-gray-100 dark:hover:bg-gray-900',
      className,
    )}
    {...props}
  >
    {children}
    <RiArrowRightSLine className="ml-auto size-4 shrink-0" aria-hidden="true" />
  </DropdownMenuPrimitives.SubTrigger>
))
DropdownMenuSubMenuTrigger.displayName = 'DropdownMenuSubMenuTrigger'

const DropdownMenuSubMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.SubContent>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.SubContent>
>(({ className, collisionPadding = 8, ...props }, ref) => (
  <DropdownMenuPrimitives.Portal>
    <DropdownMenuPrimitives.SubContent
      ref={ref}
      collisionPadding={collisionPadding}
      className={cx(
        'relative z-50 overflow-hidden rounded-md border p-1 shadow-xl shadow-black/[2.5%]',
        'min-w-32 max-h-[var(--radix-popper-available-height)]',
        'bg-white dark:bg-gray-950',
        'text-gray-900 dark:text-gray-50',
        'border-gray-200 dark:border-gray-800',
        'data-[state=closed]:animate-hide',
        'data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade',
        className,
      )}
      {...props}
    />
  </DropdownMenuPrimitives.Portal>
))
DropdownMenuSubMenuContent.displayName = 'DropdownMenuSubMenuContent'

const DropdownMenuContent = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.Content>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Content>
>(({ className, sideOffset = 8, collisionPadding = 8, align = 'center', ...props }, ref) => (
  <DropdownMenuPrimitives.Portal>
    <DropdownMenuPrimitives.Content
      ref={ref}
      className={cx(
        'relative z-50 overflow-hidden rounded-md border p-1 shadow-xl shadow-black/[2.5%]',
        'min-w-48 max-h-[var(--radix-popper-available-height)]',
        'bg-white dark:bg-gray-950',
        'text-gray-900 dark:text-gray-50',
        'border-gray-200 dark:border-gray-800',
        'data-[state=closed]:animate-hide',
        'data-[side=bottom]:animate-slideDownAndFade data-[side=left]:animate-slideLeftAndFade data-[side=right]:animate-slideRightAndFade data-[side=top]:animate-slideUpAndFade',
        className,
      )}
      sideOffset={sideOffset}
      align={align}
      collisionPadding={collisionPadding}
      {...props}
    />
  </DropdownMenuPrimitives.Portal>
))
DropdownMenuContent.displayName = 'DropdownMenuContent'

const DropdownMenuItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.Item>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Item> & {
    shortcut?: string
    hint?: string
  }
>(({ className, shortcut, hint, children, ...props }, ref) => (
  <DropdownMenuPrimitives.Item
    ref={ref}
    className={cx(
      'group/DropdownMenuItem relative flex cursor-pointer select-none items-center rounded py-1.5 pl-2 pr-1 outline-none transition-colors sm:text-sm',
      'text-gray-900 dark:text-gray-50',
      'data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 dark:data-[disabled]:text-gray-600',
      'focus-visible:bg-gray-100 dark:focus-visible:bg-gray-900',
      'hover:bg-gray-100 dark:hover:bg-gray-900',
      className,
    )}
    {...props}
  >
    {children}
    {hint && (
      <span className="ml-auto pl-2 text-sm text-gray-400 dark:text-gray-600">{hint}</span>
    )}
    {shortcut && (
      <span className="ml-auto pl-2 text-sm text-gray-400 dark:text-gray-600">{shortcut}</span>
    )}
  </DropdownMenuPrimitives.Item>
))
DropdownMenuItem.displayName = 'DropdownMenuItem'

const DropdownMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.RadioItem>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.RadioItem> & {
    iconType?: 'check' | 'radio'
  }
>(({ className, children, iconType = 'radio', ...props }, ref) => (
  <DropdownMenuPrimitives.RadioItem
    ref={ref}
    className={cx(
      'group/DropdownMenuRadioItem relative flex cursor-pointer select-none items-center gap-x-2 rounded py-1.5 pl-8 pr-1 outline-none transition-colors data-[state=checked]:font-semibold sm:text-sm',
      'text-gray-900 dark:text-gray-50',
      'data-[disabled]:pointer-events-none data-[disabled]:text-gray-400 dark:data-[disabled]:text-gray-600',
      'focus-visible:bg-gray-100 dark:focus-visible:bg-gray-900',
      'hover:bg-gray-100 dark:hover:bg-gray-900',
      className,
    )}
    {...props}
  >
    {iconType === 'radio' ? (
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <RiRadioButtonFill
          aria-hidden="true"
          className="size-full shrink-0 text-primary-500 group-data-[state=checked]/DropdownMenuRadioItem:flex group-data-[state=unchecked]/DropdownMenuRadioItem:hidden"
        />
        <RiCheckboxBlankCircleLine
          aria-hidden="true"
          className="size-full shrink-0 text-gray-300 group-data-[state=unchecked]/DropdownMenuRadioItem:flex group-data-[state=checked]/DropdownMenuRadioItem:hidden dark:text-gray-700"
        />
      </span>
    ) : (
      <span className="absolute left-2 flex size-4 items-center justify-center">
        <RiCheckLine
          aria-hidden="true"
          className="size-full shrink-0 text-gray-800 group-data-[state=checked]/DropdownMenuRadioItem:flex group-data-[state=unchecked]/DropdownMenuRadioItem:hidden dark:text-gray-200"
        />
      </span>
    )}
    {children}
  </DropdownMenuPrimitives.RadioItem>
))
DropdownMenuRadioItem.displayName = 'DropdownMenuRadioItem'

const DropdownMenuLabel = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.Label>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Label>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitives.Label
    ref={ref}
    className={cx(
      'px-2 py-2 text-xs font-medium tracking-wide',
      'text-gray-500 dark:text-gray-500',
      className,
    )}
    {...props}
  />
))
DropdownMenuLabel.displayName = 'DropdownMenuLabel'

const DropdownMenuSeparator = React.forwardRef<
  React.ElementRef<typeof DropdownMenuPrimitives.Separator>,
  React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitives.Separator>
>(({ className, ...props }, ref) => (
  <DropdownMenuPrimitives.Separator
    ref={ref}
    className={cx('-mx-1 my-1 h-px border-t border-gray-200 dark:border-gray-800', className)}
    {...props}
  />
))
DropdownMenuSeparator.displayName = 'DropdownMenuSeparator'

export {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuGroup,
  DropdownMenuSubMenu,
  DropdownMenuSubMenuTrigger,
  DropdownMenuSubMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
}
